import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Download,
  TrendingUp,
  TrendingDown,
  Shield,
  Activity,
  Calendar,
} from "lucide-react";
import type { EmailMonitoring } from "../../services/EmailHygieneService";

interface DetailedAnalyticsProps {
  monitoring: EmailMonitoring;
}

const DetailedAnalytics: React.FC<DetailedAnalyticsProps> = ({
  monitoring,
}) => {
  const handleExportData = () => {
    const csvData = [
      ["Date", "Score", "Status", "Issues"],
      ...monitoring.analysisHistory.map((history) => [
        new Date(history.timestamp).toLocaleString(),
        history.score,
        history.status,
        JSON.stringify(history.issues),
      ]),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvData.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `email-monitoring-${
        monitoring.parameterEmail
      }-${new Date().toISOString()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPerformanceMetrics = () => {
    const history = monitoring.analysisHistory;
    if (history.length < 2) return null;

    const latest = history[history.length - 1].score;
    const previous = history[history.length - 2].score;
    const improvement = latest - previous;

    const averageScore =
      history.reduce((acc, curr) => acc + curr.score, 0) / history.length;

    const volatility = Math.sqrt(
      history
        .map((h) => Math.pow(h.score - averageScore, 2))
        .reduce((a, b) => a + b, 0) / history.length
    );

    return {
      improvement,
      averageScore: Math.round(averageScore),
      volatility: Math.round(volatility * 100) / 100,
      trend: improvement >= 0 ? "improving" : "declining",
    };
  };

  const metrics = getPerformanceMetrics();

  const issueDistribution = monitoring.analysisHistory
    .flatMap((h) => Object.keys(h.issues))
    .reduce(
      (acc, issue) => ({
        ...acc,
        [issue]: (acc[issue] || 0) + 1,
      }),
      {} as Record<string, number>
    );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-yellow-DEFAULT" />
            <span className="text-sm text-text-secondary">Latest Score</span>
          </div>
          <div className="text-3xl font-bold">
            {monitoring.analysisHistory[monitoring.analysisHistory.length - 1]
              ?.score || 0}
          </div>
          {metrics && (
            <div
              className={`flex items-center gap-1 mt-2 text-sm ${
                metrics.improvement >= 0 ? "text-green-dark" : "text-red-dark"
              }`}
            >
              {metrics.improvement >= 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              <span>{Math.abs(metrics.improvement)} points</span>
            </div>
          )}
        </div>

        <div className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-yellow-DEFAULT" />
            <span className="text-sm text-text-secondary">Average Score</span>
          </div>
          <div className="text-3xl font-bold">{metrics?.averageScore || 0}</div>
          <div className="text-sm text-text-secondary mt-2">
            Volatility: {metrics?.volatility || 0}
          </div>
        </div>

        <div className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-yellow-DEFAULT" />
            <span className="text-sm text-text-secondary">Total Checks</span>
          </div>
          <div className="text-3xl font-bold">
            {monitoring.analysisHistory.length}
          </div>
          <div className="text-sm text-text-secondary mt-2">
            Since {new Date(monitoring.createdAt).toLocaleDateString()}
          </div>
        </div>

        <div className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6">
          <button
            onClick={handleExportData}
            className="h-full w-full flex flex-col items-center justify-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
          >
            <Download className="w-6 h-6" />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Score History</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={monitoring.analysisHistory.map((h) => ({
                  date: new Date(h.timestamp).toLocaleDateString(),
                  score: h.score,
                }))}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-divider dark:stroke-divider-dark"
                />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="var(--yellow-DEFAULT)"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Issue Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(issueDistribution).map(
                    ([name, value]) => ({
                      name,
                      value,
                    })
                  )}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {Object.entries(issueDistribution).map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`hsl(${
                        (index * 360) / Object.keys(issueDistribution).length
                      }, 70%, 50%)`}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedAnalytics;
